<template>
  <div :class="b()">
    <h1 :class="b('title')">
      {{ $t('l-location-finder.title') }}
    </h1>

    <!-- Reset Filter -->
    <div v-if="showResetFilterButton" :class="b('filter-reset')">
      <button :class="b('reset-button')" type="button" @click="resetFilter">
        {{ $t('l-location-finder.resetFilterLabel') }}
        <e-icon icon="i-close" size="15" />
      </button>
    </div>

    <!-- Search / Filter -->
    <aside :class="b('filters')">
      <c-google-places-autocomplete v-model="autocompleteQuery"
                                    :class="b('places')"
                                    :label="$t('l-location-finder.placesLabel')"
                                    @change="focusLocation"
      />

      <!-- Search Distance Radius -->
      <e-label :name="$t('l-location-finder.searchRadiusLabel')" :class="b('search-radius')">
        <e-select
          v-model="selectedSearchRadius"
          :options="searchRadiusOptions"
          :placeholder="$t('l-location-finder.searchRadiusPlaceholder')"
          :disabled="!userPositionIsSet"
          has-selectable-placeholder
          @change="handleChangeSearchRadius"
        />
      </e-label>

      <!-- Brands Filter (optional) -->
      <e-label
        v-if="brandOptions.length"
        tag="div"
        :name="$t('l-location-finder.filterBrandsLabel')"
        :class="b('brands')"
      >
        <e-multiselect
          v-model="selectedBrands"
          :options="brandOptions"
          :placeholder="$t('l-location-finder.filterBrandsPlaceholder')"
        />
      </e-label>

      <!-- Geo Location Trigger -->
      <button v-if="browserSupportsGeoLocation"
              :class="b('geolocation')"
              type="button"
              @click="requestCurrentPositionFromGeolocation"
      >
        {{ $t('l-location-finder.geolocationLabel') }}
        <e-loader v-if="isRequestingLocation" variant="tiny" />
        <e-icon v-else icon="i-map-pin" />
      </button>
    </aside>

    <!-- Locations List -->
    <section :class="b('locations')">
      <ul :class="b('list')">
        <li v-for="location in filteredLocations"
            :key="location.name"
        >
          <c-location-tile :location="location"
                           @click="handleUpdateDefaultSelection"
          />
        </li>
      </ul>
      <button
        v-if="selectedLocation"
        :class="b('reset-button')"
        type="button"
        @click="selectedLocation = undefined"
      >
        <e-icon icon="i-arrow--left" size="18" />
        {{ $t('l-location-finder.showAllLocations') }}
      </button>
    </section>

    <!-- Map -->
    <aside :class="b('map-container')">
      <c-google-maps
        ref="map"
        :locations="googleMapsLocations"
        :user-position="mapCenter"
        :class="b('map')"
        @marker-click="handleClickLocation"
        @map-click="handleMapClick"
      />

      <!-- Info Windows (Used to render the marker-details in the google maps) -->
      <div v-if="renderInfoWindowContainer" ref="infoWindows" :class="b('maps-info-window-container')">
        <template v-for="location in allLocations" :key="location.name">
          <c-google-maps-info-window
            :location="location"
            @click="handleUpdateDefaultSelection"
          />
        </template>
      </div>
    </aside>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, Ref } from 'vue';
  import { Layout } from '@/setup/globals';
  import getMarkerIcon from '@/helpers/google-marker-icon';
  import cGooglePlacesAutocomplete from '@/components/c-google-places-autocomplete.vue';
  import cGoogleMaps, { GoogleMapsLocation } from '@/components/c-google-maps.vue';
  import cLocationTile from '@/components/c-location-tile.vue';
  import eIcon from '@/elements/e-icon.vue';
  import eLoader from '@/elements/e-loader.vue';
  import eMultiselect, { Option } from '@/elements/e-multiselect.vue';
  import eLabel from '@/elements/e-label.vue';
  import eSelect from '@/elements/e-select.vue';
  import useNotificationStore from '@/stores/notification';
  import googleMapsApi from '@/compositions/google-maps-api';
  import setActiveLayout from '@/compositions/set-active-layout';
  import cGoogleMapsInfoWindow from '@/components/c-google-maps-info-window.vue';
  import useSessionStore from '@/stores/session';
  import useLocationFinderStore from '@/stores/location-finder';
  import { PointOfService } from '@/types/point-of-service';

  const layout = Layout.LocationFinder;

  type Setup = {
    locationFinderStore: ReturnType<typeof useLocationFinderStore>;
    sessionStore: ReturnType<typeof useSessionStore>;
    browserSupportsGeoLocation: boolean;
    infoWindows: Ref<HTMLElement | undefined>;
    map: Ref<typeof cGoogleMaps | undefined>;
  }

  type Data = {
    mapCenter?: google.maps.LatLng;
    isRequestingLocation: boolean;
    autocompleteQuery: string;
    geocoder?: google.maps.Geocoder;
    googleMapsLocations: GoogleMapsLocation[];
    selectedLocation?: string;
    renderInfoWindowContainer: boolean;
    selectedBrands: string[];
    selectedSearchRadius: string;
    userPositionIsSet: boolean;
  }

  type TemporaryAddressData = {
    zip?: string;
    city?: string;
    country?: string;
  }

  const BRAND_QUERY_PARAM = 'brand';
  const SEARCH_RADIUS_OPTIONS = [10, 20, 50, 100];
  const SEARCH_RADIUS_UNIT = 'km';

  /**
   * Renders a layout that allows to search for map locations.
   */
  export default defineComponent({
    name: layout,
    components: {
      eSelect,
      eLabel,
      eMultiselect,
      cGoogleMapsInfoWindow,
      eLoader,
      eIcon,
      cGoogleMaps,
      cGooglePlacesAutocomplete,
      cLocationTile,
    },

    // components: {},

    // props: {},
    // emits: {},

    setup(): Setup {
      setActiveLayout(layout);

      return {
        locationFinderStore: useLocationFinderStore(),
        sessionStore: useSessionStore(),
        browserSupportsGeoLocation: !!navigator.geolocation,
        infoWindows: ref(),
        map: ref(),
      };
    },
    data(): Data {
      return {
        isRequestingLocation: false,
        mapCenter: undefined,
        geocoder: undefined,
        autocompleteQuery: '',
        googleMapsLocations: [],
        selectedLocation: undefined,
        renderInfoWindowContainer: true,
        selectedBrands: [],
        selectedSearchRadius: '',
        userPositionIsSet: false,
      };
    },

    computed: {
      /**
       * Needed as a computed to register the "watcher".
       */
      allLocations(): PointOfService[] {
        return this.locationFinderStore.locations;
      },

      filteredLocations(): PointOfService[] {
        if (!this.selectedLocation && this.selectedBrands.length <= 0 && !this.selectedSearchRadius) {
          return this.allLocations;
        }

        return this.allLocations
          .filter((location) => {
            if (this.selectedLocation) {
              return location.name === this.selectedLocation;
            }

            let isValidResult = true;

            if (this.selectedSearchRadius) {
              isValidResult = !!(location.distanceKm && location.distanceKm <= parseInt(this.selectedSearchRadius, 10));
            }

            if (this.selectedBrands.length) {
              isValidResult = !!location.brands?.some(brand => this.selectedBrands.includes(brand.code));
            }

            return isValidResult;
          });
      },

      brandOptions(): Option[] {
        return this.locationFinderStore.brands.map(brand => ({
          value: brand.code,
          label: brand.name,
        }));
      },

      showResetFilterButton(): boolean {
        return !!(this.selectedBrands.length || this.userPositionIsSet);
      },

      searchRadiusOptions(): Option[] {
        return SEARCH_RADIUS_OPTIONS.map(value => ({
          value: value.toString(),
          label: `${value} ${SEARCH_RADIUS_UNIT}`,
        }));
      },
    },
    watch: {
      filteredLocations: {
        handler(locations) {
          if (locations) {
            // It's needed to re-render the info windows because the map removes the DOM elements when showing an info window.
            this.renderInfoWindowContainer = false;

            // $nextTick is needed to make sure the new infoWindows are already rendered in the DOM.
            this.$nextTick(() => {
              this.renderInfoWindowContainer = true;
              this.googleMapsLocations = this.getMappedLocations();
            });
          }
        },
        immediate: true,
      },
    },

    // beforeCreate() {},
    created() {
      this.readSelectedBrandFromUrl();
    },
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      getMappedLocations(): GoogleMapsLocation[] {
        return this.filteredLocations.map(location => ({
          lat: location.geoPoint?.latitude,
          lng: location.geoPoint?.longitude,
          geocode: location.name,
          title: location.displayName,
          icon: getMarkerIcon(location.favorite),
          infoWindow: this.findInfoWindow(location),
        }));
      },

      findInfoWindow(location: PointOfService): Promise<Element | string> {
        return new Promise((resolve) => {
          this.$nextTick(() => {
            resolve(this.infoWindows?.querySelector(`div[data-name="${location.name}"]`) || '');
          });
        });
      },

      focusLocation(place: google.maps.GeocoderResult): void {
        const location = place?.geometry?.location;

        if (location) {
          this.mapCenter = location;
        }

        this.userPositionIsSet = true;
      },

      requestCurrentPositionFromGeolocation(): void {
        this.isRequestingLocation = true;

        navigator.geolocation.getCurrentPosition(
          this.setCenterFromGeolocationPosition,
          this.showGeolocationError,
          {
            enableHighAccuracy: true,
            timeout: 3000,
            maximumAge: 0,
          }
        );
      },

      setCenterFromGeolocationPosition(position: GeolocationPosition): void {
        this.isRequestingLocation = false;

        this.mapCenter = new google.maps.LatLng({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });

        if (!this.geocoder) {
          googleMapsApi().then((maps) => {
            this.geocoder = new maps.Geocoder();

            this.updateLocationQueryWithMapCenter();
          });
        } else {
          this.updateLocationQueryWithMapCenter();
        }
      },

      showGeolocationError(error: GeolocationPositionError): void {
        console.error(error.message); // eslint-disable-line no-console

        this.isRequestingLocation = false;

        useNotificationStore().showNotification({
          message: this.$t('l-location-finder.browserLocationError'),
          type: 'error',
          expire: true,
        });
      },

      updateLocationQueryWithMapCenter(): void {
        this.geocoder?.geocode({ location: this.mapCenter }, (results) => {
          /* eslint-disable camelcase */
          const { address_components, formatted_address = '' } = results?.[0] || {};

          if (Array.isArray(address_components)) {
            const { zip, city, country } = this.getAddressFromGeocoderResult(address_components);

            if (zip && city && country) {
              this.autocompleteQuery = `${zip} ${city}, ${country}`;

              return;
            }
          }

          this.autocompleteQuery = formatted_address;
          /* eslint-enable camelcase */
        });
      },

      getAddressFromGeocoderResult(addressComponents: google.maps.GeocoderAddressComponent[]): TemporaryAddressData {
        return addressComponents.reduce((accumulator, component) => {
          if (component.types.includes('postal_code')) {
            accumulator.zip = component.long_name;
          } else if (component.types.includes('locality')) {
            accumulator.city = component.long_name;
          } else if (component.types.includes('country')) {
            accumulator.country = component.long_name;
          }

          return accumulator;
        }, {} as TemporaryAddressData);
      },

      handleClickLocation({ location }: { location: GoogleMapsLocation }): void {
        this.selectedLocation = location.geocode;
      },

      handleMapClick(latLng: google.maps.LatLng): void {
        this.locationFinderStore.apiFetchStores({ lat: latLng.lat(), lng: latLng.lng() });
        this.userPositionIsSet = true;
      },

      handleUpdateDefaultSelection(location: PointOfService): void {
        const favorite = !location.favorite;

        this.sessionStore.apiUpdateFavoriteStore(location, favorite)
          .then(() => {
            this.locationFinderStore.locations = this.locationFinderStore.locations.map(locationItem => ({
              ...locationItem,
              favorite: favorite && locationItem.name === location.name,
            }));
          });
      },

      readSelectedBrandFromUrl(): void {
        const selectedBrand = new URLSearchParams(window.location?.search || '').get(BRAND_QUERY_PARAM);

        if (selectedBrand) {
          this.selectedBrands = [selectedBrand];
        }
      },

      resetFilter(): void {
        this.selectedBrands = [];
        this.selectedSearchRadius = '';
        this.autocompleteQuery = '';
        this.mapCenter = undefined;
        this.userPositionIsSet = false;
        this.geocoder = undefined;
        this.filteredLocations = this.allLocations;
        this.map?.fitBounds();
        this.map?.resetTempMarker();
      },

      handleChangeSearchRadius(): void {
        this.map?.fitBounds();
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .l-location-finder {
    display: grid;
    grid-gap: variables.$spacing--50 variables.$spacing--20;
    grid-template-areas:
      'title'
      'filter'
      'reset'
      'map'
      'locations';
    grid-template-columns: 1fr;

    @include mixins.media(md) {
      grid-template-areas:
        'title reset'
        'filter filter'
        'locations map';
      grid-template-columns: 1fr 2fr;
    }

    &__title {
      @include mixins.heading-h1();

      grid-area: title;
      margin: 0;
    }

    &__filter-reset {
      display: flex;
      grid-area: reset;
      justify-content: flex-end;
      align-items: flex-end;
    }

    &__filters {
      display: flex;
      grid-area: filter;
      gap: variables.$spacing--15;
      flex-direction: column;

      @include mixins.media(md) {
        @include mixins.grid();

        align-items: end;
      }
    }

    &__reset-button {
      display: flex;
      grid-column-gap: variables.$spacing--10;
      align-items: center;
      cursor: pointer;
      font-weight: variables.$font-weight--bold;

      &:hover,
      &:focus {
        color: variables.$color-primary--1;
      }
    }

    &__locations {
      display: flex;
      grid-area: locations;
      grid-row-gap: variables.$spacing--25;
      flex-direction: column;
    }

    &__list {
      display: flex;
      gap: variables.$spacing--25;
      flex-direction: column;

      @include mixins.media(md) {
        gap: variables.$spacing--35;
      }
    }

    &__map-container {
      grid-area: map;

      @include mixins.media(md) {
        position: sticky;
        top: calc(var(--header-height, 0) + #{variables.$spacing--20});
        height: 50vh;
      }
    }

    &__places,
    &__search-radius,
    &__brands {
      grid-column: span 4;

      @include mixins.media(lg) {
        grid-column: span 3;
      }
    }

    &__geolocation {
      display: flex;
      grid-column: span 4;
      gap: variables.$spacing--10;
      align-items: center;
      cursor: pointer;

      @include mixins.media(lg) {
        grid-column: span 3;
      }

      &:hover,
      &:focus {
        color: variables.$color-primary--1;
      }

      .e-loader {
        margin: 0;
      }
    }

    &__maps-info-window-container {
      display: none;
    }
  }

  .pac-container {
    @include mixins.font(variables.$font-size--18);

    .pac-item,
    .pac-item-query {
      font-size: inherit;
    }

    .pac-item {
      padding: variables.$spacing--5 variables.$spacing--15;
      border-left: 4px solid transparent;

      &:hover,
      &:focus,
      &.pac-item-selected {
        border-left-color: variables.$color-primary--1;
      }
    }

    .pac-icon-marker {
      display: none;
    }
  }
</style>
